import { Contract, UpdateContractContactsBodyDto } from '@ekt-group/general-purpose-api-interfaces';
import { useQueryClient } from '@tanstack/react-query';
import { Modal, notification } from 'antd';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/ui/Button';

import ContactInformationForm, { ContactFormRef, ContactFormValues } from './ContactInformationForm';

import { useUpdateContactForm } from '../../api/use-update-contact-form';

type ContactInformationModalProps = {
  contract: Contract;
  isModalOpen: boolean;
  handleClose: () => void;
  required?: boolean;
  afterClose?: () => void;
};

const ContactInformationModal = ({ contract, isModalOpen, handleClose, required, afterClose }: ContactInformationModalProps) => {
  const { t } = useTranslation('contractModal');
  const queryClient = useQueryClient();
  const updateContactInformationMutation = useUpdateContactForm(contract.id);
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef<ContactFormRef>(null);

  const handleSubmit = async (formData: ContactFormValues) => {
    setIsLoading(true);
    try {
      const payload: UpdateContractContactsBodyDto = {
        fullAddress: formData.fullAddress,
        email: formData.emails[0]?.value || null,
        email2: formData.emails[1]?.value || null,
        email3: formData.emails[2]?.value || null,
        invoiceEmail: formData.invoiceEmails[0]?.value || null,
        invoiceEmail2: formData.invoiceEmails[1]?.value || null,
        phone: formData.phones[0]?.value || null,
        phone2: formData.phones[1]?.value || null,
        phone3: formData.phones[2]?.value || null,
        phone4: formData.invoiceEmails[3]?.value,
        countyId: Number(formData.countyId),
        cityId: Number(formData.cityId),
        villageId: Number(formData.villageId),
        street: formData.street,
        houseNumber: formData.houseNumber,
        flatNumber: formData.flatNumber,
        invoiceSms: formData.invoiceSms,
      };

      await updateContactInformationMutation.mutateAsync(payload);
      await queryClient.invalidateQueries({ queryKey: ['contracts'] });
      notification.success({
        message: t('contactInformationSaved'),
        description: `${t('contractNumber')} - ${contract.contractNumber}`,
        duration: 3,
      });
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleOk = () => {
    const formData = formRef.current?.getFormData();
    handleSubmit(formData);
  };

  return (
    <Modal
      centered
      styles={{
        body: {
          overflowY: 'auto',
          height: '70vh',
        },
        content: {
          justifySelf: 'center',
          borderRadius: 24,
          width: '100%',
        },
      }}
      width={{
        lg: 1006,
        md: 696,
        sm: 696,
        xs: '100%',
      }}
      rootClassName="w-[1006px] sm:w-[696px] w-[320px]"
      open={isModalOpen}
      onCancel={handleClose}
      title={
        <div className="flex gap-4 items-center text-[24px] text-bold">
          <span className="text-gray-10">{t('editContactInfo')}</span>
          <span className="text-[#A7AD2A]">L{contract.contractNumber}</span>
        </div>
      }
      closable={!required}
      maskClosable={!required}
      cancelButtonProps={{ hidden: required }}
      afterClose={afterClose}
      footer={
        <div className="flex gap-4 h-12">
          <Button onClick={handleClose} variant="secondaryAlt" disabled={required} className="flex-auto !max-h-12">
            {t('cancel')}
          </Button>
          <Button
            disabled={updateContactInformationMutation.isPending}
            pending={updateContactInformationMutation.isPending}
            onClick={handleOk}
            className="flex-auto !max-h-12"
          >
            {t('ok')}
          </Button>
        </div>
      }
      okButtonProps={{
        loading: isLoading,
      }}
    >
      <ContactInformationForm key={contract.id} ref={formRef} contract={contract} />
    </Modal>
  );
};

export default ContactInformationModal;
